import React, { useState, useRef } from "react";
import { IconQrcode, IconQrcodeOff, IconLoader2 } from "@tabler/icons-react";
import { useGuests } from "@/providers/GuestsProvider";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";
import { useTranslations } from "next-intl";
import { AnimatePresence } from "framer-motion";
import { opensPartySheet } from "@/lib/guests/party";
import { checkInFailureText } from "@/lib/guests/checkInFailureText";
import { PCheckInParty } from "@/components/PCheckInParty/PCheckInParty";

type TCCCheckInOutProps = {
  guestId: string;
};

export const TableCellContentCheckInOut: React.FC<TCCCheckInOutProps> =
  React.memo(function TableCellContentCheckInOut({ guestId }) {
    const { guests, checkInGuests, eventId } = useGuests();
    // Optional variant: TableCellContent is reused by render sites outside
    // the event scope (no CapabilitiesProvider) — fail closed (hidden) there.
    const { can, fieldAllowlist } = useCapabilitiesOptional();
    const { blockIfPreviewing } = usePreviewWriteGuard();
    const t = useTranslations("guestDetails");
    const tCheckIn = useTranslations("checkIn");
    const tParty = useTranslations("checkInParty");
    const tErrors = useTranslations("errors");
    // A ref as well as state (`TCCAction.tsx:64-66`): two clicks inside one
    // tick read the same `loading` value, so state alone cannot stop a double
    // submit — and neither can the `disabled` attribute it drives.
    const submitRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [partyOpen, setPartyOpen] = useState(false);

    // Party-grouped list: a companion is not a top-level row, it hangs under
    // its head (`hydrateParties` fills those with the full projection, so they
    // carry `checkedIn`). Looking only at the top level made every companion
    // row read "not checked in" and turned its button into a one-way door.
    const guest =
      guests.find((g) => g.guestId === guestId) ??
      guests
        .flatMap((g) => g.companions ?? [])
        .find((c) => c.guestId === guestId);
    const checkedIn = !!guest?.checkedIn;
    // Nobody else to bring along ⇒ the click stays what it always was: one
    // write, no dialog. Only a guest with companions (or a companion, who has a
    // main guest) gets the party sheet.
    const opensParty = !!guest && opensPartySheet(guest, can);

    // Capability gate (audit N12) — replaces the legacy role-floor check.
    if (!can("guests:check-in")) return null;
    // Field gate: the button reads `checkedIn` above to know whether it checks
    // IN or OUT. A CUSTOM role whose allowlist withholds the field gets the row
    // masked — every guest reads "not checked in", the button always says
    // "Check in", and nobody can ever be checked OUT from here. No button at
    // all is the honest state; the template editor names the field to grant.
    // (`Array.isArray`, not `!== null`: the optional fallback and older
    // hosts/tests carry no allowlist at all.)
    if (Array.isArray(fieldAllowlist) && !fieldAllowlist.includes("checkedIn")) {
      return null;
    }

    return (
      <div className="flex flex-col items-start gap-1">
        <button
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg body-3-medium transition-colors border outline-quaternary surface-primary font-primary hover:surface-tertiary"
          onClick={async (e) => {
            e.stopPropagation();
            // Read-only preview: explain instead of firing a masked write.
            if (blockIfPreviewing()) return;
            if (submitRef.current) return;
            // Opening the sheet is an early return that never reaches a
            // `finally`, so the latch must not be taken on that path — doing so
            // left the ref `true` for the life of the component and the button
            // dead as soon as the sheet was closed once.
            if (opensParty) {
              setError(null);
              setWarning(null);
              setPartyOpen(true);
              return;
            }
            submitRef.current = true;
            setLoading(true);
            setError(null);
            setWarning(null);
            try {
              // ONE lane for one guest and for twelve.
              //
              // This used to go through `checkInGuest` → `GuestHandling` →
              // `handleErrorServer`, and that chain cannot carry the reason for
              // this endpoint's 409s: the body is `{error: "CAPACITY_EXCEEDED"}`
              // with no `errorCode` and no `message` (GuestResource.java:1583),
              // so `handleErrorServer` falls through to axios's own string, and
              // `handleError`/`throwServerError` rebuild the Error WITHOUT
              // `.response` — deliberately, they say so. Reading
              // `err.response.data.error` here was therefore always `undefined`
              // and `msg.includes("CAPACITY_EXCEEDED")` always false: a
              // capacity block reached the operator as "Request failed with
              // status code 409", untranslated, in all 57 locales.
              //
              // `checkInGuests` already solves this for the party sheet — it
              // calls the CLIENT, keeps the body, and classifies. Routing the
              // solo click through it also ends a second split: only the party
              // lane broadcast `guestDataUpdated`, so a guest-detail popup left
              // open beside the list went stale for solo guests and not for
              // families.
              const report = await checkInGuests([guestId], !checkedIn);
              const result = report.results[0];
              if (!result || result.status === "already") {
                // Nothing wrong: someone at the door got there first.
              } else if (result.status === "done") {
                if (result.warning === "capacity") {
                  setWarning(tCheckIn("capacityExceeded"));
                }
              } else {
                // The same words the party sheet uses for the same refusal.
                setError(
                  checkInFailureText(result.error, {
                    checkIn: tCheckIn,
                    errors: tErrors,
                    party: tParty,
                  }),
                );
              }
            } catch {
              // `runPartyCheckIn` reports refusals in the report rather than
              // throwing; anything that still lands here is the transport
              // giving out, which has no per-guest reason to show.
              setError(tErrors("unknown"));
            } finally {
              // A real `finally`, which the comment above the latch already
              // claimed. As trailing statements these were equivalent only
              // because the `catch` happens to swallow everything — one
              // `return` or one re-throw added later and the button dies.
              submitRef.current = false;
              setLoading(false);
            }
          }}
          disabled={loading}
        >
          {loading ? (
            <IconLoader2 size={14} className="animate-spin" />
          ) : checkedIn ? (
            <IconQrcodeOff size={14} />
          ) : (
            <IconQrcode size={14} />
          )}
          <span className="whitespace-nowrap">
            {checkedIn ? t("checkOut") : t("checkIn")}
          </span>
        </button>
        {error && (
          <span className="body-4-regular font-error">{error}</span>
        )}
        {warning && (
          <span className="body-4-regular font-orange">{warning}</span>
        )}
        <AnimatePresence>
          {partyOpen && guest && (
            <PCheckInParty
              eventId={eventId}
              row={guest}
              // ONE direction for the whole party: the one this row's button
              // offered. Never re-derived per person — a companion who is
              // already inside must not be checked out by a check-in click.
              checkIn={!checkedIn}
              onClose={() => setPartyOpen(false)}
            />
          )}
        </AnimatePresence>
      </div>
    );
  });
